import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from '@/components/store/Store';
import { Api } from '@/services/Api';
import { Skin } from '@/declarations/models/Skin';
import { formatAPITimestamp } from '@/utils/dates';
import { AvailableDesigns } from '@/declarations/models/Designs';
import { useSnackbar } from 'notistack';
import { Design } from '@/declarations/models/Design';
import Container from '@/components/Container';
import { Box, Typography, Button } from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import CloseIcon from '@mui/icons-material/Close';
import { ChildrenProp } from '../declarations/ChildrenProp';

export const StylesView: FC<ChildrenProp> = () => {
  const { t: tComponents } = useTranslation('components');
  const {
    state: { selectedSite },
  } = useStore();
  const store = useStore();

  const [selectedDesign, setSelectedDesign] = useState<Design[] | null>(null);
  const [availableDesigns, setAvailableDesigns] = useState<AvailableDesigns>({
    designs: { global: [], owner: [], site: [] },
  });
  const [level, setLevelState] = useState<'global' | 'owner' | 'site'>('site');
  const { enqueueSnackbar } = useSnackbar();
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const { state } = useStore();
  const currentUser = state?.currentUser;
  const isSuperAdmin = currentUser?.user?.is_super_admin;

  function setLevel(value: 'global' | 'owner' | 'site') {
    setLevelState(value);
  }

  useEffect(() => {
    const ctx = Api.getAllDesignsForSite(selectedSite?.id || 0);
    ctx.fetchDirect(null).then((res) => {
      setSelectedDesign(res);
    });

    const ctx2 = Api.getAllDesigns(selectedSite?.id || 0);
    ctx2.fetch().then((data) => {
      setAvailableDesigns((data[0] as AvailableDesigns) || []);
    });

    return () => {
      ctx.abort();
      ctx2.abort();
    };
  }, [selectedSite]);

  // Remove design from selection
  const handleRemoveDesign = (designId: number) => {
    if (selectedDesign) {
      const updatedDesigns = selectedDesign.filter((design) => design.id !== designId);
      setSelectedDesign(updatedDesigns);
      setIsDirty(true);
    }
  };

  // Move design up in the list
  const handleMoveUp = (index: number) => {
    if (selectedDesign && index > 0) {
      const updatedDesigns = [...selectedDesign];
      const temp = updatedDesigns[index];
      updatedDesigns[index] = updatedDesigns[index - 1];
      updatedDesigns[index - 1] = temp;
      setSelectedDesign(updatedDesigns);
      setIsDirty(true);
    }
  };

  // Move design down in the list
  const handleMoveDown = (index: number) => {
    if (selectedDesign && index < selectedDesign.length - 1) {
      const updatedDesigns = [...selectedDesign];
      const temp = updatedDesigns[index];
      updatedDesigns[index] = updatedDesigns[index + 1];
      updatedDesigns[index + 1] = temp;
      setSelectedDesign(updatedDesigns);
      setIsDirty(true);
    }
  };

  const handleSaveDesign = async () => {
    if (selectedDesign) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const ctx = Api.saveSiteDesign(selectedSite?.id || 0, { designs: { active: selectedDesign } });
      await ctx
        .fetchDirect(null)
        .then((res) => {
          if (res && selectedSite?.id) {
            enqueueSnackbar('Oppdatering vellykket', { variant: 'success' });
            setIsDirty(false);

            const ctx3 = Api.getAllDesignsForSite(selectedSite.id);
            ctx3.fetchDirect(null).then((res2) => {
              if (res2) {
                setSelectedDesign(res2);

                const skins: Skin[] = [];
                const themes: Skin[] = [];
                // eslint-disable-next-line no-restricted-syntax
                for (const design of res2) {
                  if (Array.isArray(design.skins)) {
                    skins.push(...design.skins);
                  }
                  if (Array.isArray(design.themes)) {
                    themes.push(...design.themes);
                  }
                }

                store.setSiteSkins(skins);
                store.setSiteThemes(themes);
              }
            });
          }
        })
        .catch(() => enqueueSnackbar('Oppdatering FEIL', { variant: 'error' }));
    }
  };

  return (
    <Container fullWidth left column>
      <Container left fullWidth p={2} top mt={2} column>
        <Typography sx={{ mt: 2, fontSize: 'xx-large' }} variant='h1'>
          {tComponents('MainMenu.styles')}
        </Typography>
        <Typography>{tComponents('MainMenu.stylesDescription')}</Typography>
        <div>
          <ul>
            <li>
              <a href={`${selectedSite?.url}/m24-design`} target='_blank' rel='noopener noreferrer'>
                {`${selectedSite?.url}/m24-design`}
              </a>
            </li>
          </ul>
        </div>

        {isSuperAdmin && (
          <Container className='superadmin-color' gap={2} top fullWidth left sx={{ padding: '20px' }}>
            <Container sx={{ background: '#fff', padding: '10px', width: '100%' }} left gap={2}>
              <Container left top fullWidth column>
                <Container left wrap>
                  <Button
                    variant={level === 'site' ? 'contained' : 'outlined'}
                    color='secondary'
                    onClick={() => {
                      setLevel('site');
                    }}>
                    Lokale
                  </Button>
                  <Button
                    variant={level === 'owner' ? 'contained' : 'outlined'}
                    color='secondary'
                    onClick={() => {
                      setLevel('owner');
                    }}>
                    Felles/konsolidert
                  </Button>
                  <Button
                    variant={level === 'global' ? 'contained' : 'outlined'}
                    color='secondary'
                    onClick={() => {
                      setLevel('global');
                    }}>
                    Globale
                  </Button>
                </Container>

                <Typography sx={{ mt: 2, fontWeight: 700 }}>TILGJENGELIGE STILSETT (kun publiserte)</Typography>

                <div
                  style={{
                    background: '#efefef',
                    padding: '10px',
                    height: '450px',
                    overflowY: 'auto',
                    width: '100%',
                    display: 'flex',
                    gap: '6px',
                    flexDirection: 'column',
                  }}>
                  {availableDesigns &&
                    availableDesigns?.designs?.[level]
                      .filter((design) => design.status === 'published' || design.status === 'general')
                      .sort((a, b) => new Date(b?.updated_at ?? 0).getTime() - new Date(a.updated_at ?? 0).getTime())
                      .map((design) => (
                        <Box
                          key={design.id}
                          sx={{
                            position: 'relative',
                            padding: '10px',
                            border: '1px solid #ccc',
                            backgroundColor: selectedDesign?.find((d) => d.id === design.id) ? '#d0f0c0' : '#fff',
                          }}>
                          <Typography variant='h6'>{design.title}</Typography>
                          Nivå: {design.is_global && 'Globalt'} {design.owner_id && 'Eier'}{' '}
                          {design.site_id && 'Nettsted'} / Skins: {design.skins.length || 0}, Themes:{' '}
                          {design.themes.length || 0} / Oppdatert:{' '}
                          {formatAPITimestamp(design.updated_at || '', 'date') || '-'}
                          <Container left top gap={1} sx={{ position: 'absolute', top: '10px', right: '10px' }}>
                            {!selectedDesign?.find((d) => d.id === design.id) && (
                              <Button
                                variant='outlined'
                                size='small'
                                onClick={() => {
                                  if (!selectedDesign?.find((d) => d.id === design.id)) {
                                    setSelectedDesign((prev) => [...(prev || []), design]);
                                    setIsDirty(true);
                                  }
                                }}>
                                Velg
                              </Button>
                            )}
                          </Container>
                        </Box>
                      ))}
                </div>
              </Container>
              <Container left top fullWidth column>
                <Container right fullWidth>
                  <Button
                    variant='contained'
                    onClick={handleSaveDesign}
                    color={isDirty ? 'success' : 'primary'}
                    disabled={!isDirty}>
                    Lagre
                  </Button>
                </Container>

                <Typography sx={{ mt: 2, fontWeight: 700 }}>VALGTE STILSETT ({selectedDesign?.length || 0})</Typography>
                <div
                  style={{
                    background: '#d0f0c0',
                    padding: '10px',
                    height: '450px',
                    overflowY: 'auto',
                    width: '100%',
                    display: 'flex',
                    gap: '6px',
                    flexDirection: 'column',
                  }}>
                  {selectedDesign?.map((design, index) => (
                    <Box key={design.id} sx={{ padding: '10px', border: '1px solid #ccc', backgroundColor: '#fff' }}>
                      <Container left top gap={1} sx={{ position: 'relative' }}>
                        <Container column left top gap={1}>
                          <Typography variant='h6'>{design.title}</Typography>
                          Nivå: {design.is_global && 'Globalt'} {design.owner_id && 'Eier'}{' '}
                          {design.site_id && 'Nettsted'} / Skins: {design.skins.length || 0}, Themes:{' '}
                          {design.themes.length || 0}/ Oppdatert:{' '}
                          {formatAPITimestamp(design.updated_at || '', 'date') || '-'}
                        </Container>

                        <Container left top gap={1} sx={{ position: 'absolute', top: '0', right: '0' }}>
                          <Button
                            variant='outlined'
                            color='error'
                            size='small'
                            onClick={() => handleRemoveDesign(design.id || 0)}>
                            <CloseIcon fontSize='small' />
                          </Button>
                          <Button
                            variant='outlined'
                            size='small'
                            disabled={index === 0}
                            onClick={() => handleMoveUp(index)}>
                            <ArrowUpwardIcon fontSize='small' />
                          </Button>
                          <Button
                            variant='outlined'
                            size='small'
                            disabled={index === (selectedDesign?.length || 0) - 1}
                            onClick={() => handleMoveDown(index)}>
                            <ArrowDownwardIcon fontSize='small' />
                          </Button>
                        </Container>
                      </Container>
                    </Box>
                  ))}
                </div>
              </Container>
            </Container>
          </Container>
        )}

        <Container left fullWidth top mt={2}>
          <Typography sx={{ fontWeight: 700 }}>SKINS</Typography>
        </Container>
        <Container left wrap>
          {selectedDesign?.flatMap((design) =>
            Array.isArray(design.skins)
              ? design.skins.map((skin) => (
                  <Box
                    key={`${skin.class}-${design.id}`}
                    sx={{
                      width: '200px',
                      height: '200px',
                      padding: '10px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: skin.hex,
                      color: skin.txt,
                      position: 'relative',
                    }}>
                    <Box
                      sx={{
                        textAlign: 'center',
                      }}>
                      <strong>{skin.title}</strong>

                      <Typography sx={{ my: 1, fontSize: '12px' }}>
                        .{skin.class}
                        <br />
                        txt:{skin.txt} / bg: {skin.hex} | <span style={{ backgroundColor: skin.rgb }}>{skin.rgb}</span>
                      </Typography>
                    </Box>
                    <Typography sx={{ position: 'absolute', bottom: '5px', right: '5px', my: 1, fontSize: '10px' }}>
                      {design.title}
                    </Typography>
                  </Box>
                ))
              : [],
          )}
        </Container>
        <Container left fullWidth top mt={2}>
          <Typography sx={{ fontWeight: 700 }}>THEMES</Typography>
        </Container>
        <Container left wrap>
          {selectedDesign?.flatMap((design) =>
            Array.isArray(design.themes)
              ? design.themes.map((theme) => (
                  <Box
                    key={`${theme.class}-${design.id}`}
                    sx={{
                      width: '200px',
                      height: '200px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: theme.hex,
                      color: theme.txt,
                      position: 'relative',
                    }}>
                    <Box
                      sx={{
                        textAlign: 'center',
                      }}>
                      <strong>{theme.title}</strong>
                      <Typography sx={{ my: 1, fontSize: '12px' }}>
                        .{theme.class}
                        <br />
                        {theme.hex} <br />
                        <span style={{ backgroundColor: theme.rgb }}>{theme.rgb}</span>
                      </Typography>
                    </Box>
                    <Typography sx={{ position: 'absolute', bottom: '5px', right: '5px', my: 1, fontSize: '10px' }}>
                      {design.title}
                    </Typography>
                  </Box>
                ))
              : [],
          )}
        </Container>
      </Container>
    </Container>
  );
};

export default StylesView;
